import React, { FC, ReactNode, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SettingsIcon from '@mui/icons-material/Settings';
import TuneIcon from '@mui/icons-material/Tune';
import GavelIcon from '@mui/icons-material/Gavel';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';

interface NavItem {
  label: string;
  path: string;
  icon: ReactNode;
}

const navItems: NavItem[] = [
  { label: 'Home', path: '/', icon: <HomeIcon /> },
  { label: 'Store', path: '/shop', icon: <StorefrontIcon /> },
  { label: 'Settings', path: '/settings', icon: <SettingsIcon /> },
  { label: 'Preferences', path: '/preferences', icon: <TuneIcon /> },
  { label: 'Earn', path: '/earn', icon: <PaidOutlinedIcon /> },
  { label: 'Referral Program', path: '/referrals', icon: <GroupAddOutlinedIcon /> },
  { label: 'Creator Program', path: '/creator-program', icon: <AutoAwesomeOutlinedIcon /> },
  { label: 'Promote', path: '/promote', icon: <CampaignOutlinedIcon /> },
  { label: 'Help', path: '/help', icon: <HelpOutlineIcon /> },
  { label: 'Report Abuse', path: '/report-abuse', icon: <FlagOutlinedIcon /> },
  { label: 'Policies', path: '/policies', icon: <GavelIcon /> },
];

interface WebSideNavProps {
  // 'rail' renders the persistent left column (md and up).
  // 'menu' renders a hamburger + drawer that replaces the rail below md.
  mode: 'rail' | 'menu';
}

const WebSideNav: FC<WebSideNavProps> = ({ mode }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));

  const handleNav = (path: string) => {
    setDrawerOpen(false);
    navigate(path);
  };

  // Guide side-nav idiom: pill rows, active row gets the accent-dim tonal fill.
  const renderList = () => (
    <List sx={{ px: 1 }}>
      {navItems.map((item) => {
        const selected = location.pathname === item.path;
        return (
          <ListItemButton
            key={item.path}
            selected={selected}
            onClick={() => handleNav(item.path)}
            sx={{
              borderRadius: 'var(--sh-full)',
              mb: 0.5,
              color: selected ? theme.palette.primary.main : theme.tokens.colors.text2,
              '&.Mui-selected': {
                backgroundColor: theme.tokens.colors.redSoft,
                '&:hover': { backgroundColor: theme.tokens.colors.redSoft },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 44, color: selected ? 'primary.main' : theme.tokens.colors.text3 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 'fontWeightBold' }} />
          </ListItemButton>
        );
      })}
    </List>
  );

  if (mode === 'rail') {
    if (isCompact) {
      return null;
    }
    return (
      <Paper
        elevation={0}
        sx={{
          minWidth: 220,
          maxWidth: 240,
          borderRadius: 'var(--radius-none)',
          mr: 'var(--space-stack-tight)',
          alignSelf: 'stretch',
          // Transparent rail floats over the aurora; only a hairline separates it.
          backgroundColor: 'transparent',
          borderRight: `1px solid ${theme.tokens.colors.glassLine}`,
          // Offset below the fixed glass bar that overlays the top of the body.
          pt: 'calc(var(--layout-top-bar-height) + 8px)',
          pb: 1,
        }}
      >
        {renderList()}
      </Paper>
    );
  }

  // mode === 'menu'
  if (!isCompact) {
    return null;
  }
  return (
    <>
      <IconButton
        color="primary"
        aria-label="Open navigation menu"
        onClick={() => setDrawerOpen(true)}
        size="large"
        sx={{ margin: 'var(--space-inline-compact)', padding: 'var(--space-inline-compact)' }}
      >
        <MenuIcon />
      </IconButton>
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 260 } }}
      >
        {renderList()}
      </Drawer>
    </>
  );
};

export default WebSideNav;
